import { createContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { login, verify, impersonateEmployee } from "../features/auth/api/auth";
import { fetchEffectivePermissions } from "../features/roles/api/RolesApi";

export type AppUser = {
  id?: number;
  name: string;
  email: string;
  roles?: string[];
};

export type ImpersonatedEmployee = {
  name: string;
  code: string;
};

export type EffectivePermissions = Record<string, { can_view: boolean; can_edit: boolean }>;

export const VerifyContext = createContext<{
  user: AppUser | null;
  authCheckLoading: boolean;
  superAdmin: boolean;
  authUserType: "admin" | "employee";
  setUser: (user: AppUser | null) => void;
  loginUser: (
    email: string,
    password: string,
    userType?: "admin" | "employee"
  ) => Promise<{ success: boolean; message?: string }>;
  canAccessEmployees: boolean;
  canAccessEmployeeEvaluation: boolean;
  canAccessCvProcessor: boolean;
  canAccessInterviewQa: boolean;
  canAccessJobBoard: boolean;
  canAccessHiringContacts: boolean;
  canAccessPromptLibrary: boolean;
  canAccessCrm: boolean;
  canAccessSheets: boolean;
  canAccessDocuments: boolean;
  canAccessMeetings: boolean;
  isTechnicalManager: boolean;
  effectivePermissions: EffectivePermissions;
  impersonatedEmployee: ImpersonatedEmployee | null;
  impersonateAs: (employeeCode: string) => Promise<void>;
  leaveImpersonation: () => Promise<boolean>;
}>({
  user: null,
  setUser: () => {},
  superAdmin: false,
  authUserType: "employee",
  loginUser: async () => ({ success: false }),
  authCheckLoading: false,
  canAccessEmployees: false,
  canAccessEmployeeEvaluation: false,
  canAccessCvProcessor: false,
  canAccessInterviewQa: false,
  canAccessJobBoard: false,
  canAccessHiringContacts: false,
  canAccessPromptLibrary: false,
  canAccessCrm: false,
  canAccessSheets: false,
  canAccessDocuments: false,
  canAccessMeetings: false,
  isTechnicalManager: false,
  effectivePermissions: {},
  impersonatedEmployee: null,
  impersonateAs: async () => {},
  leaveImpersonation: async () => false,
});

export const VerifyContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [authCheckLoading, setAuthCheckLoading] = useState(true);
  const [authUserType, setAuthUserType] = useState<"admin" | "employee">(() => {
    const raw = localStorage.getItem("authUserType");
    return raw === "admin" || raw === "employee" ? raw : "employee";
  });
  const [effectivePermissions, setEffectivePermissions] = useState<EffectivePermissions>({});
  const [impersonatedEmployee, setImpersonatedEmployee] = useState<ImpersonatedEmployee | null>(null);

  const superAdmin = user !== null && authUserType === "admin";
  const isEmployee = user !== null && authUserType === "employee";
  const resolvedPermissions: EffectivePermissions = isEmployee ? effectivePermissions : {};

  const canAccessEmployees = superAdmin || !!resolvedPermissions["employees"]?.can_view;
  const canAccessEmployeeEvaluation = superAdmin || !!resolvedPermissions["evaluation"]?.can_view;
  const canAccessCvProcessor = superAdmin || !!resolvedPermissions["cv_processor"]?.can_view;
  const canAccessInterviewQa = superAdmin || !!resolvedPermissions["interview_qa"]?.can_view;
  const canAccessJobBoard = superAdmin || !!resolvedPermissions["job_board"]?.can_view;
  const canAccessHiringContacts = superAdmin || !!resolvedPermissions["hiring_contacts"]?.can_view;
  const canAccessPromptLibrary = superAdmin || !!resolvedPermissions["prompt_library"]?.can_view;
  const canAccessCrm = superAdmin || !!resolvedPermissions["crm"]?.can_view;
  // Unlike every other page permission, Sheets has no role-level gate —
  // access to any individual sheet is controlled entirely by whether an
  // admin has shared it with this employee, so every employee sees the
  // nav item regardless of role.
  const canAccessSheets = superAdmin || isEmployee;
  // Documents mirrors Sheets' access model exactly (see comment above) —
  // per-document sharing is the only gate, not a role-level page permission.
  const canAccessDocuments = superAdmin || isEmployee;
  const canAccessMeetings = superAdmin || !!resolvedPermissions["meetings"]?.can_view;
  const isTechnicalManager = isEmployee && !!user?.roles?.includes("Technical Manager");

  const loginUser = useCallback(async (
    email: string,
    password: string,
    userType: "admin" | "employee" = "employee"
  ) => {
    const { ok, data } = await login(email, password, userType);
    if (!ok || !data.success) {
      return { success: false, message: data.message };
    }

    localStorage.setItem("token", data.token);
    localStorage.setItem("authUserType", userType);
    setAuthUserType(userType);

    const profileResult = await verify(data.token, userType);
    if (!profileResult.ok || !profileResult.data.success) {
      localStorage.removeItem("token");
      localStorage.removeItem("authUserType");
      return { success: false, message: "Could not load profile. Please try again." };
    }

    setUser({
      ...profileResult.data.user,
      roles: profileResult.data.roles || [],
    });

    if (userType === "employee") {
      try {
        const perms = await fetchEffectivePermissions();
        setEffectivePermissions(perms);
      } catch {
        setEffectivePermissions({});
      }
    }

    return { success: true };
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");

      if (token) {
        const storedType =
          (localStorage.getItem("authUserType") as "admin" | "employee") ||
          "employee";
        setAuthUserType(storedType);
        const { ok, data } = await verify(token, storedType);
        if (ok && data.success) {
          setUser({ ...data.user, roles: data.roles || [] });
          if (storedType === "employee") {
            try {
              const perms = await fetchEffectivePermissions();
              setEffectivePermissions(perms);
            } catch {
              setEffectivePermissions({});
            }
          }
          // Restore impersonation banner if we're in an impersonation session
          const adminToken = localStorage.getItem("hrms_admin_token");
          if (adminToken) {
            const stored = localStorage.getItem("hrms_impersonated_employee");
            if (stored) {
              try { setImpersonatedEmployee(JSON.parse(stored)); } catch {}
            }
          }
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("authUserType");
          localStorage.removeItem("hrms_admin_token");
          localStorage.removeItem("hrms_impersonated_employee");
          setUser(null);
          setEffectivePermissions({});
          setImpersonatedEmployee(null);
        }
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("authUserType");
        localStorage.removeItem("hrms_admin_token");
        localStorage.removeItem("hrms_impersonated_employee");
        setUser(null);
        setImpersonatedEmployee(null);
      }

      setAuthCheckLoading(false);
    };

    checkAuth();
  }, []);

  // Clear stale impersonation state whenever the user is logged out from any path
  // (handleLogOut, token expiry, leaveImpersonation failure) so the banner never
  // reappears on the next login.
  useEffect(() => {
    if (user === null) {
      setImpersonatedEmployee(null);
    }
  }, [user]);

  const impersonateAs = useCallback(async (employeeCode: string) => {
    const result = await impersonateEmployee(employeeCode);
    if (!result.ok) throw new Error(result.data.message || "Impersonation failed");

    const { employee_token, employee } = result.data;

    const adminToken = localStorage.getItem("token")!;
    const impersonated: ImpersonatedEmployee = { name: employee.name, code: employee.employee_code };

    localStorage.setItem("hrms_admin_token", adminToken);
    localStorage.setItem("hrms_impersonated_employee", JSON.stringify(impersonated));
    localStorage.setItem("token", employee_token);
    localStorage.setItem("authUserType", "employee");
    setAuthUserType("employee");

    try {
      const profileResult = await verify(employee_token, "employee");
      if (!profileResult.ok || !profileResult.data.success) {
        throw new Error("Could not load employee profile");
      }
      setUser({ ...profileResult.data.user, roles: profileResult.data.roles || [] });

      try {
        const perms = await fetchEffectivePermissions();
        setEffectivePermissions(perms);
      } catch {
        setEffectivePermissions({});
      }

      setImpersonatedEmployee(impersonated);
    } catch (err) {
      localStorage.setItem("token", adminToken);
      localStorage.setItem("authUserType", "admin");
      localStorage.removeItem("hrms_admin_token");
      localStorage.removeItem("hrms_impersonated_employee");
      setAuthUserType("admin");
      throw err;
    }
  }, []);

  const leaveImpersonation = useCallback(async (): Promise<boolean> => {
    const adminToken = localStorage.getItem("hrms_admin_token");
    if (!adminToken) return false;

    localStorage.setItem("token", adminToken);
    localStorage.setItem("authUserType", "admin");
    setAuthUserType("admin");
    setEffectivePermissions({});
    setImpersonatedEmployee(null);

    let ok = false;
    let data: any = null;
    try {
      const result = await verify(adminToken, "admin");
      ok = result.ok;
      data = result.data;
    } catch {
      // Network error — admin token is restored in localStorage; page refresh will self-heal
      localStorage.removeItem("hrms_admin_token");
      localStorage.removeItem("hrms_impersonated_employee");
      return false;
    }

    localStorage.removeItem("hrms_admin_token");
    localStorage.removeItem("hrms_impersonated_employee");

    if (ok && data?.success) {
      setUser({ ...data.user, roles: [] });
      return true;
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("authUserType");
      setUser(null);
      return false;
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      loginUser,
      superAdmin,
      authUserType,
      authCheckLoading,
      canAccessEmployees,
      canAccessEmployeeEvaluation,
      canAccessCvProcessor,
      canAccessInterviewQa,
      canAccessJobBoard,
      canAccessHiringContacts,
      canAccessPromptLibrary,
      canAccessCrm,
      canAccessSheets,
      canAccessDocuments,
      canAccessMeetings,
      isTechnicalManager,
      effectivePermissions: resolvedPermissions,
      impersonatedEmployee,
      impersonateAs,
      leaveImpersonation,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loginUser, superAdmin, authUserType, authCheckLoading, canAccessEmployees, canAccessEmployeeEvaluation, canAccessCvProcessor, canAccessInterviewQa, canAccessJobBoard, canAccessHiringContacts, canAccessPromptLibrary, canAccessCrm, canAccessSheets, canAccessDocuments, canAccessMeetings, isTechnicalManager, effectivePermissions, impersonatedEmployee, impersonateAs, leaveImpersonation]
  );

  return (
    <VerifyContext.Provider value={contextValue}>
      {children}
    </VerifyContext.Provider>
  );
};
